/**
 * Schedule Day Component
 *
 * Renders a day row group in the schedule table.
 * The date column spans the full height of all stops for that day.
 * Uses CSS Grid with 4 columns: date | time | location | actions.
 *
 * @element peach-schedule-day
 */

import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { ScheduleDay } from "../services/calendar-data";
import "./schedule-stop";

const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;
const MONTH_NAMES = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
] as const;

@customElement("peach-schedule-day")
export class ScheduleDayComponent extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Object })
  declare day: ScheduleDay;

  @property({ type: Object })
  declare searchPoint: { lat: number; lng: number } | null;

  constructor() {
    super();
    this.day = null as unknown as ScheduleDay;
    this.searchPoint = null;
  }

  /**
   * Calculate distance between two lat/lng points in miles using the Haversine formula
   */
  private calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 3958.8;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /**
   * Render the date header for mobile (horizontal inline)
   *
   * @remarks
   * Uses sticky positioning below the month header (52px offset).
   * Stays visible while scrolling through that day's stops.
   */
  private renderMobileDateHeader() {
    const d = this.day.dateObj;
    const dayName = DAY_NAMES[d.getDay()];
    const dateNum = d.getDate();
    const monthName = MONTH_NAMES[d.getMonth()];

    return html`
      <div class="flex items-center gap-3 px-4 py-3 bg-surface border-b-2 border-muted sticky top-[52px] z-10">
        <span class="text-base font-bold text-default font-heading uppercase tracking-wide">
          ${dayName}
        </span>
        <span class="text-xl font-bold text-brand font-heading leading-none">
          ${dateNum}
        </span>
        <span class="text-base font-bold text-default font-heading uppercase tracking-wide">
          ${monthName}
        </span>
      </div>
    `;
  }

  /**
   * Render the date column for desktop (vertical stacked on left)
   *
   * @remarks
   * Uses sticky positioning to lock the date below the month header
   * as the user scrolls through stops for that day.
   */
  private renderDesktopDateColumn() {
    const d = this.day.dateObj;
    const dayName = DAY_NAMES[d.getDay()];
    const dateNum = d.getDate();
    const monthName = MONTH_NAMES[d.getMonth()];

    return html`
      <div class="flex flex-col items-center justify-start p-4 bg-cream flex-shrink-0 w-24 sticky top-[52px] self-start z-10">
        <span class="text-xs font-bold text-default font-heading tracking-wide uppercase">
          ${dayName}
        </span>
        <span class="text-3xl font-bold text-brand font-heading leading-none my-1">
          ${dateNum}
        </span>
        <span class="text-xs font-bold text-default font-heading tracking-wide uppercase">
          ${monthName}
        </span>
      </div>
    `;
  }

  render() {
    if (!this.day) return null;

    const stops = this.day.stops.map((stop) => {
      const distance = this.searchPoint
        ? this.calculateDistance(
            this.searchPoint.lat,
            this.searchPoint.lng,
            stop.location.lat,
            stop.location.lng
          )
        : null;

      return html`
        <peach-schedule-stop
          .stop=${stop}
          .distance=${distance}
        ></peach-schedule-stop>
      `;
    });

    return html`
      <div class="border-t-2 border-muted">
        <div class="md:hidden">
          ${this.renderMobileDateHeader()}
          <div class="flex flex-col gap-3 px-4 py-3">
            ${stops}
          </div>
        </div>

        <div class="hidden md:flex">
          ${this.renderDesktopDateColumn()}
          <div class="flex flex-col gap-3 p-4 flex-1">
            ${stops}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "peach-schedule-day": ScheduleDayComponent;
  }
}
